import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faTimes,
	faWindowMaximize,
	faWindowMinimize,
} from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import DomHandler from '@app/utils/DomHandler';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';

let DialogComponentId = 0;
const UniqueComponentId = (prefix = 'id_') => `${prefix}${DialogComponentId++}`;

class Dialog extends Component {
	static defaultProps = {
		id: null,
		header: null,
		footer: null,
		visible: false,
		modal: true,
		onHide: null,
		onShow: null,
		contentStyle: null,
		closeOnEscape: true,
		dismissableMask: false,
		rtl: false,
		closable: true,
		style: null,
		className: null,
		showHeader: true,
		appendTo: null,
		baseZIndex: 0,
		maximizable: false,
		blockScroll: true,
		maximize: false,
	};

	static propTypes = {
		id: PropTypes.string,
		header: PropTypes.any,
		footer: PropTypes.any,
		visible: PropTypes.bool,
		modal: PropTypes.bool,
		onHide: PropTypes.func.isRequired,
		onShow: PropTypes.func,
		contentStyle: PropTypes.object,
		closeOnEscape: PropTypes.bool,
		dismissableMask: PropTypes.bool,
		rtl: PropTypes.bool,
		closable: PropTypes.bool,
		style: PropTypes.object,
		className: PropTypes.string,
		showHeader: PropTypes.bool,
		appendTo: PropTypes.object,
		baseZIndex: PropTypes.number,
		maximizable: PropTypes.bool,
		blockScroll: PropTypes.bool,
		children: PropTypes.any,
		maximize: PropTypes.bool,
	};

	constructor(props) {
		super(props);
		this.state = {
			maximized: false,
		};
		this.onClose = this.onClose.bind(this);
		this.toggleMaximize = this.toggleMaximize.bind(this);

		this.id = this.props.id || UniqueComponentId();
	}

	onClose(event) {
		this.props.onHide();
		event.preventDefault();
	}

	hide() {
		this.unbindMaskClickListener();
		this.unbindGlobalListeners();

		this.props.onHide();

		if (this.props.modal) {
			this.disableModality();
		}

		if (this.state.maximized) {
			DomHandler.removeClass(document.body, 'ui-overflow-hidden');
		}
	}

	focus() {
		let focusable = DomHandler.findSingle(this.container, 'button');
		if (focusable) {
			focusable.focus();
		}
	}

	show() {
		this.bindGlobalListeners();

		if (this.props.onShow) {
			this.props.onShow();
		}

		this.container.style.zIndex = String(
			this.props.baseZIndex + DomHandler.generateZIndex(),
		);
		this.focus();

		if (this.props.modal) {
			this.enableModality();
		}

		if (this.state.maximized) {
			DomHandler.removeClass(document.body, 'ui-overflow-hidden');
		}
	}

	toggleMaximize(event) {
		this.setState({
			maximized: !this.state.maximized,
		});
		event.preventDefault();
	}

	maximize() {
		DomHandler.addClass(this.container, 'ui-dialog-maximized');
		DomHandler.addClass(document.body, 'ui-overflow-hidden');

		const diffHeight =
			DomHandler.getOuterHeight(this.headerElement) +
			DomHandler.getOuterHeight(this.footerElement);
		this.contentElement.style.minHeight =
			'calc(100vh - ' + diffHeight + 'px)';
	}

	restoreMaximize() {
		DomHandler.removeClass(this.container, 'ui-dialog-maximized');
		DomHandler.removeClass(document.body, 'ui-overflow-hidden');
		this.contentElement.style.minHeight = 'auto';
	}

	enableModality() {
		if (!this.mask) {
			this.mask = document.createElement('div');
			this.mask.style.zIndex = String(
				parseInt(this.container.style.zIndex, 10) - 1,
			);
			DomHandler.addMultipleClasses(
				this.mask,
				'ui-component-overlay ui-dialog-mask',
			);

			if (this.props.closable && this.props.dismissableMask) {
				this.maskClickListener = event => {
					this.onClose(event);
				};

				this.mask.addEventListener('click', this.maskClickListener);
			}

			document.body.appendChild(this.mask);

			if (this.props.blockScroll) {
				DomHandler.addClass(document.body, 'ui-overflow-hidden');
			}
		}
	}

	disableModality() {
		if (this.mask) {
			this.unbindMaskClickListener();

			document.body.removeChild(this.mask);
			if (this.props.blockScroll) {
				DomHandler.removeClass(document.body, 'ui-overflow-hidden');
			}
			this.mask = null;
		}
	}

	unbindMaskClickListener() {
		if (this.maskClickListener) {
			this.mask.removeEventListener('click', this.maskClickListener);
			this.maskClickListener = null;
		}
	}

	bindGlobalListeners() {
		if (this.props.closeOnEscape && this.props.closable) {
			this.bindDocumentEscapeListener();
		}
	}

	unbindGlobalListeners() {
		this.unbindDocumentEscapeListener();
	}

	bindDocumentEscapeListener() {
		this.documentEscapeListener = event => {
			if (event.which === 27) {
				if (
					parseInt(this.container.style.zIndex, 10) ===
					DomHandler.getCurrentZIndex()
				) {
					this.onClose(event);
				}
			}
		};
		document.addEventListener('keydown', this.documentEscapeListener);
	}

	unbindDocumentEscapeListener() {
		if (this.documentEscapeListener) {
			document.removeEventListener(
				'keydown',
				this.documentEscapeListener,
			);
			this.documentEscapeListener = null;
		}
	}

	componentDidMount() {
		if (this.props.visible) {
			this.show();
			this.currentHeight = DomHandler.getOuterHeight(this.container);
		}
		if (this.props.maximize) {
			this.maximize();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevProps.visible !== this.props.visible) {
			if (this.props.visible) this.show();
			else this.hide();
		}

		if (prevState.maximized !== this.state.maximized) {
			if (this.state.maximized) {
				this.maximize();
			} else {
				this.restoreMaximize();
			}
		}
	}

	componentWillUnmount() {
		this.disableModality();
		this.unbindGlobalListeners();
		this.unbindMaskClickListener();
	}

	renderCloseIcon() {
		if (this.props.closable) {
			return (
				<button
					className="ui-dialog-titlebar-icon ui-dialog-titlebar-close ui-link"
					onClick={this.onClose}
					aria-label="close"
				>
					{/*	<img src="/mvc/content/icn/close.svg" alt="close" />*/}
					<FontAwesomeIcon
						icon={faTimes}
						className="ui-dialog-titlebar-close-icon"
					/>
				</button>
			);
		} else {
			return null;
		}
	}

	renderMaximizeIcon() {
		if (this.props.maximizable) {
			const { maximized } = this.state;
			return (
				<button
					className="ui-dialog-titlebar-icon ui-dialog-titlebar-maximize p-link"
					onClick={this.toggleMaximize}
				>
					<FontAwesomeIcon
						icon={maximized ? faWindowMinimize : faWindowMaximize}
						className="ui-dialog-titlebar-maximize-icon"
					/>
				</button>
			);
		} else {
			return null;
		}
	}

	renderHeader() {
		if (this.props.showHeader) {
			let closeIcon = this.renderCloseIcon();
			let maximizeIcon = this.renderMaximizeIcon();

			return (
				<div
					ref={el => (this.headerElement = el)}
					className="ui-dialog-titlebar"
				>
					<span id={this.id + '_label'} className="ui-dialog-title">
						{this.props.header}
					</span>
					<div className="ui-dialog-titlebar-icons">
						{maximizeIcon}
						{closeIcon}
					</div>
				</div>
			);
		} else {
			return null;
		}
	}

	renderContent() {
		return (
			<div
				ref={el => (this.contentElement = el)}
				className="ui-dialog-content"
				style={this.props.contentStyle}
			>
				{this.props.children}
			</div>
		);
	}

	renderFooter() {
		if (this.props.footer) {
			return (
				<div
					ref={el => (this.footerElement = el)}
					className="ui-dialog-footer"
				>
					{this.props.footer}
				</div>
			);
		} else {
			return null;
		}
	}

	renderElement() {
		const className = classNames(
			'ui-dialog ui-component',
			this.props.className,
			{
				'ui-dialog-rtl': this.props.rtl,
				'ui-dialog-visible': this.props.visible,
			},
		);

		const header = this.renderHeader();
		const content = this.renderContent();
		const footer = this.renderFooter();

		return (
			<CSSTransition
				classNames="ui-dialog"
				timeout={{ enter: 150, exit: 75 }}
				in={this.props.visible}
			>
				<div
					id={this.id}
					className={className}
					style={this.props.style}
					ref={el => (this.container = el)}
					aria-labelledby={this.id + '_label'}
					role="dialog"
				>
					{header}
					{content}
					{footer}
				</div>
			</CSSTransition>
		);
	}

	render() {
		const element = this.renderElement();

		if (this.props.appendTo)
			return ReactDOM.createPortal(element, this.props.appendTo);
		else return element;
	}
}

export default Dialog;
